import { useState, useEffect, useCallback, useRef } from 'react';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import { useInvoices } from '../contexts/InvoicesContext';
import { api, getActiveWorkspaceId } from '../api/client';
import {
  buildBriefingData,
  fetchEventProposals,
  buildAfternoonCheckInData,
  buildEveningWrapUpData,
  fetchWeather,
  generateAISummary,
  generateAfternoonAISummary,
  generateEveningAISummary,
  getTeamFlags,
} from '../services/briefingService';

const PREFS_KEY = 'notificationPrefs';

function loadBriefingPrefs() {
  try {
    const stored = JSON.parse(localStorage.getItem(PREFS_KEY) || '{}');
    return {
      briefingEnabled:   stored.briefingEnabled  ?? true,
      briefingTime:      stored.briefingTime     || '07:30',
      briefingDelivery:  stored.briefingDelivery || 'in-app',
      briefingWeather:   stored.briefingWeather  ?? true,
      afternoonEnabled:  stored.afternoonEnabled  ?? true,
      afternoonTime:     stored.afternoonTime     || '12:00',
      afternoonDelivery: stored.afternoonDelivery || 'in-app',
      eveningEnabled:    stored.eveningEnabled    ?? true,
      eveningTime:       stored.eveningTime       || '17:00',
      eveningDelivery:   stored.eveningDelivery   || 'in-app',
    };
  } catch {
    return {
      briefingEnabled: true,  briefingTime: '07:30',  briefingDelivery: 'in-app', briefingWeather: true,
      afternoonEnabled: true, afternoonTime: '12:00', afternoonDelivery: 'in-app',
      eveningEnabled: true,   eveningTime: '17:00',   eveningDelivery: 'in-app',
    };
  }
}

function todayKey() { return new Date().toDateString(); }

/** morning | afternoon | evening based on current hour. */
export function getBriefingMode() {
  const h = new Date().getHours();
  if (h < 12) return 'morning';
  if (h < 17) return 'afternoon';
  return 'evening';
}

function cacheKey(mode, userId) {
  // Scope the cache to the active workspace — otherwise the first workspace to
  // generate a briefing today "wins" and its content shows in the others.
  // getActiveWorkspaceId() is the exact value sent as the X-Active-Workspace
  // header, so the key always matches the workspace the data was fetched under.
  return `briefing_${mode}_${todayKey()}_${userId}_${getActiveWorkspaceId() || 'none'}`;
}

function isoDateStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function buildBriefingHtml(data, mode) {
  const title = mode === 'morning' ? 'Morning Briefing'
              : mode === 'afternoon' ? 'Afternoon Check-in'
              : 'Evening Wrap-up';
  const paragraphs = (data.aiSummary || '')
    .split('\n').filter(Boolean)
    .map(l => `<p style="margin:0 0 12px">${l}</p>`).join('');
  return `<!DOCTYPE html><html><body style="font-family:sans-serif;max-width:600px;margin:0 auto;padding:24px"><h2 style="margin:0 0 8px">${title}</h2><p style="color:#666;margin:0 0 20px">${data.greeting?.dateStr || ''}</p>${paragraphs}</body></html>`;
}

/**
 * useMorningBriefing (mode-aware)
 *
 *   < 12:00  → Morning Briefing
 *  12–17:00  → Afternoon Check-in
 *   ≥ 17:00  → Evening Wrap-up
 *
 * Each mode is generated once per day. Briefings are persisted to the
 * database for cross-device sync and cached in localStorage for instant load.
 * Email is sent automatically when the user's delivery preference is set.
 */
export function useMorningBriefing({ tasks = [], googleEvents = [], icalEvents = [], outlookEvents = [] }) {
  const { currentUser, notifications, workspaces, activeWorkspaceId } = useCurrentUser();
  const { invoices }    = useInvoices();

  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const workspaceType   = activeWorkspace?.type || null;

  const [mode,     setMode]     = useState(getBriefingMode);
  const [briefing, setBriefing] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [prefs,    setPrefs]    = useState(loadBriefingPrefs);

  const dataRef       = useRef({ tasks, googleEvents, icalEvents, outlookEvents, invoices });
  const modeRef       = useRef(mode);
  const prefsRef      = useRef(prefs);
  const generatingRef = useRef(false);
  const userIdRef     = useRef(currentUser?.id);
  const wsTypeRef     = useRef(workspaceType);

  useEffect(() => {
    dataRef.current = { tasks, googleEvents, icalEvents, outlookEvents, invoices };
  }, [tasks, googleEvents, icalEvents, outlookEvents, invoices]);

  useEffect(() => { prefsRef.current = prefs; }, [prefs]);
  useEffect(() => { userIdRef.current = currentUser?.id; }, [currentUser?.id]);
  useEffect(() => { wsTypeRef.current = workspaceType; }, [workspaceType]);

  // ── Load prefs from API; update localStorage so the next cold load is fresh ──
  useEffect(() => {
    api('/api/users/me').then(res => {
      if (!res?.ok) return null;
      return res.json();
    }).then(data => {
      if (!data) return;
      const n = data?.preferences?.notifications || {};
      const merged = {
        briefingEnabled:   n.briefingEnabled  ?? true,
        briefingTime:      n.briefingTime     || '07:30',
        briefingDelivery:  n.briefingDelivery || 'in-app',
        briefingWeather:   n.briefingWeather  ?? true,
        afternoonEnabled:  n.afternoonEnabled  ?? true,
        afternoonTime:     n.afternoonTime     || '12:00',
        afternoonDelivery: n.afternoonDelivery || 'in-app',
        eveningEnabled:    n.eveningEnabled    ?? true,
        eveningTime:       n.eveningTime       || '17:00',
        eveningDelivery:   n.eveningDelivery   || 'in-app',
      };
      localStorage.setItem(PREFS_KEY, JSON.stringify(merged));
      setPrefs(merged);
    }).catch(() => {});
  }, []);

  // ── On mount (and on user change): localStorage first, then DB for cross-device
  useEffect(() => {
    if (!currentUser?.id) return;
    const uid = currentUser.id;
    const m = getBriefingMode();
    const cached = localStorage.getItem(cacheKey(m, uid));
    if (cached) {
      try { setBriefing(JSON.parse(cached)); } catch {}
    }

    api('/api/briefing/today').then(res => {
      if (!res?.ok) return null;
      return res.json();
    }).then(data => {
      if (!data?.briefings?.length) return;
      data.briefings.forEach(b => {
        try {
          const parsed = JSON.parse(b.content);
          localStorage.setItem(cacheKey(b.mode, uid), JSON.stringify(parsed));
          if (b.mode === getBriefingMode()) setBriefing(parsed);
        } catch {}
      });
    }).catch(() => {});
  }, [currentUser?.id]);

  // ── Core generation function ───────────────────────────────────────────────
  const generate = useCallback(async () => {
    if (!currentUser || generatingRef.current) return;

    const m   = modeRef.current;
    const key = cacheKey(m, currentUser.id);

    // Don't regenerate a cached briefing for this mode today.
    const existing = localStorage.getItem(key);
    if (existing) {
      try { setBriefing(JSON.parse(existing)); } catch {}
      return;
    }

    generatingRef.current = true;
    setLoading(true);

    try {
      const p        = prefsRef.current;
      const wsType   = wsTypeRef.current;
      const isBusiness = wsType === 'business';
      const { tasks: t, googleEvents: g, icalEvents: ic, outlookEvents: o, invoices: inv } = dataRef.current;

      let data;

      // Email-detected event proposals — workspace-scoped fetch, shared by all
      // three modes; filled before the AI summary so the prose can mention them.
      const detectedEvents = await fetchEventProposals();

      if (m === 'morning') {
        // Count only THIS workspace's low-priority notifications, matching the
        // Dashboard's workspace filter (untagged → shown/counted everywhere).
        const activeWs = getActiveWorkspaceId();
        const lowCount = (notifications || []).filter(n => {
          if (['urgent','high','normal'].includes(n.priority) || n.dismissed) return false;
          const wsId = n.data?.workspaceId;
          return !wsId || wsId === activeWs;
        }).length;
        data = buildBriefingData(currentUser, t, g, ic, o, [], inv, wsType);
        data.lowPriorityNotificationCount = lowCount;
        data.mode = 'morning';
        data.eventProposals = detectedEvents;
        // Team flags read the workspace-scoped member roster — business only.
        if (isBusiness) {
          data.teamFlags = await getTeamFlags(currentUser, t);
        }
        if (p.briefingWeather && currentUser?.lat && currentUser?.lon) {
          data.weather = await fetchWeather(currentUser.lat, currentUser.lon, currentUser.timezone || 'auto');
        }
        data.aiSummary = await generateAISummary(data, currentUser?.language);

      } else if (m === 'afternoon') {
        data = buildAfternoonCheckInData(currentUser, t, g, ic, o, [], inv);
        data.eventProposals = detectedEvents;
        data.aiSummary = await generateAfternoonAISummary(data, currentUser?.language);

      } else {
        data = buildEveningWrapUpData(currentUser, t, g, ic, o, [], inv, wsType);
        data.eventProposals = detectedEvents;
        data.aiSummary = await generateEveningAISummary(data, currentUser?.language);
      }

      localStorage.setItem(key, JSON.stringify(data));
      setBriefing(data);

      // ── Persist to DB for cross-device sync ──────────────────────────────────
      api('/api/briefing/save', {
        method: 'POST',
        body: JSON.stringify({ mode: m, content: JSON.stringify(data), date: isoDateStr() }),
      }).catch(() => {});

      // ── Auto email if delivery pref is 'email' or 'both' ─────────────────────
      const deliveryKey = m === 'morning' ? 'briefingDelivery'
                        : m === 'afternoon' ? 'afternoonDelivery'
                        : 'eveningDelivery';
      const delivery = p[deliveryKey] || 'in-app';
      if ((delivery === 'email' || delivery === 'both') && currentUser?.email) {
        api('/api/briefing/send-email', {
          method: 'POST',
          body: JSON.stringify({
            briefingHtml:   buildBriefingHtml(data, m),
            recipientEmail: currentUser.email,
            date:           data.greeting?.dateStr || isoDateStr(),
          }),
        }).catch(() => {});
      }

    } catch (err) {
      console.error('[Briefing] Generation failed:', err);
    } finally {
      setLoading(false);
      generatingRef.current = false;
    }
  }, [currentUser]);

  // ── Scheduler — polls every 60 s, detects mode transitions ───────────────────
  useEffect(() => {
    if (!prefs.briefingEnabled) return;

    function check() {
      const now         = new Date();
      const currentMode = getBriefingMode();

      // Detect mode transition (e.g. clock just crossed noon).
      if (currentMode !== modeRef.current) {
        modeRef.current = currentMode;
        setMode(currentMode);
        setExpanded(false);

        const cached = localStorage.getItem(cacheKey(currentMode, userIdRef.current));
        if (cached) {
          try { setBriefing(JSON.parse(cached)); } catch { setBriefing(null); }
        } else {
          setBriefing(null);
        }
      }

      const alreadyDone = !!localStorage.getItem(cacheKey(currentMode, userIdRef.current));
      if (alreadyDone) return;

      const p = prefsRef.current;
      let isPastTrigger = false;

      if (currentMode === 'morning') {
        const [th, tm] = (p.briefingTime || '07:30').split(':').map(Number);
        isPastTrigger = now.getHours() > th || (now.getHours() === th && now.getMinutes() >= tm);
      } else if (currentMode === 'afternoon') {
        const [th, tm] = (p.afternoonTime || '12:00').split(':').map(Number);
        isPastTrigger = now.getHours() > th || (now.getHours() === th && now.getMinutes() >= tm);
      } else {
        const [th, tm] = (p.eveningTime || '17:00').split(':').map(Number);
        isPastTrigger = now.getHours() > th || (now.getHours() === th && now.getMinutes() >= tm);
      }

      if (isPastTrigger) generate();
    }

    check();
    const interval = setInterval(check, 60000);
    return () => clearInterval(interval);
  }, [generate, prefs.briefingEnabled]);

  // ── Actions ───────────────────────────────────────────────────────────────
  async function resendEmail() {
    if (!briefing || !currentUser?.email) return;
    try {
      await api('/api/briefing/send-email', {
        method: 'POST',
        body: JSON.stringify({
          briefingHtml:   buildBriefingHtml(briefing, mode),
          recipientEmail: currentUser.email,
          date:           briefing.greeting?.dateStr || isoDateStr(),
        }),
      });
    } catch { /* non-critical */ }
  }

  return {
    briefing,
    loading,
    expanded,
    setExpanded,
    resendEmail,
    shouldShow:   prefs.briefingEnabled,
    generate,
    mode,
    briefingTime: prefs.briefingTime || '07:30',
  };
}
